import React, {useState, useEffect} from 'react';
import CreateVersionDialog from "./CreateVersionDialog";

// Type definitions based on your backend code
export const VersionStatus = {
    DEVELOPMENT: 'development',
    RELEASED: 'released',
    DEPRECATED: 'deprecated',
    DRAFT: 'draft'
};

// VersioningPanel with currentVersionId prop support
const VersioningPanel = ({
                             projectId,
                             canisterId,
                             userPrincipal,
                             userActor,
                             onVersionChange,
                             onVersionCreate,
                             onVersionStatusChange,
                             onError,
                             onSuccess,
                             currentVersionId, // Added prop to support external version state
                             initializeProjectVersion, // New prop to initialize first version
                             workingCopyBaseVersionId // New prop to identify working copy base version
                         }) => {
    // State management
    const [versions, setVersions] = useState([]);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showVersionDetails, setShowVersionDetails] = useState(false);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [showCreateVersionDialog, setShowCreateVersionDialog] = useState(false);
    const [isCurrentFilesMode, setIsCurrentFilesMode] = useState(currentVersionId === null);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [versionToDelete, setVersionToDelete] = useState(null);
    // New state for promote to working copy
    const [showPromoteConfirmation, setShowPromoteConfirmation] = useState(false);
    const [versionToPromote, setVersionToPromote] = useState(null);
    const [overwriteExisting, setOverwriteExisting] = useState(true);

    // Form state for creating a new version
    const [newVersion, setNewVersion] = useState({
        major: 0,
        minor: 1,
        patch: 0,
        prerelease: '',
        build: '',
        description: '',
        releaseNotes: '',
        parentVersionId: ''
    });

    useEffect(() => {
        fetchVersions();

        // Update current files mode state when currentVersionId changes
        setIsCurrentFilesMode(currentVersionId === null);

        console.log(`[VersionPanel] currentVersionId changed to: ${currentVersionId === null ? 'null (Current Files)' : currentVersionId}`);
    }, [projectId, currentVersionId]);

    // Function to fetch all versions for this project
    const fetchVersions = async () => {
        try {
            setIsLoading(true);

            // Call the backend method to get project versions
            const result = await userActor.getProjectVersions(projectId);

            if ('ok' in result) {
                // Sort versions by semantic version (newest first)
                const sortedVersions = result.ok.sort((a, b) => {
                    const versionA = a.semanticVersion;
                    const versionB = b.semanticVersion;

                    // Convert BigInt to Number safely
                    const majorA = typeof versionA.major === 'bigint' ? Number(versionA.major) : versionA.major;
                    const majorB = typeof versionB.major === 'bigint' ? Number(versionB.major) : versionB.major;

                    if (majorA !== majorB) return majorB - majorA;

                    const minorA = typeof versionA.minor === 'bigint' ? Number(versionA.minor) : versionA.minor;
                    const minorB = typeof versionB.minor === 'bigint' ? Number(versionB.minor) : versionB.minor;

                    if (minorA !== minorB) return minorB - minorA;

                    const patchA = typeof versionA.patch === 'bigint' ? Number(versionA.patch) : versionA.patch;
                    const patchB = typeof versionB.patch === 'bigint' ? Number(versionB.patch) : versionB.patch;

                    return patchB - patchA;
                });

                setVersions(sortedVersions);

                // Set current version - check for externally provided version ID first
                if (sortedVersions.length > 0) {
                    if (currentVersionId === null) {
                        // Current Files mode - no specific version selected
                        console.log('[VersionPanel] Using Current Files mode (no version)');
                        setCurrentVersion(null);
                    } else if (currentVersionId && sortedVersions.some(v => v.id === currentVersionId)) {
                        // Use the provided current version if it exists
                        const versionObj = sortedVersions.find(v => v.id === currentVersionId);
                        setCurrentVersion(versionObj);
                        console.log(`[VersionPanel] Using externally provided version: ${currentVersionId}`);
                    } else {
                        // If no specific version is selected or provided version is not found,
                        // use the first/latest version
                        setCurrentVersion(sortedVersions[0]);
                        // Notify parent about this selected version
                        onVersionChange(sortedVersions[0].id);
                        console.log(`[VersionPanel] Using default latest version: ${sortedVersions[0].id}`);
                    }
                }
            } else {
                onError(result.err);
            }
        } catch (error) {
            onError(`Failed to fetch versions: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to delete a version
    const deleteVersion = async (versionId) => {
        try {
            setIsLoading(true);

            // Call the backend method to delete the version
            const result = await userActor.deleteProjectVersion(versionId);

            if ('ok' in result) {
                onSuccess(`Version deleted successfully`);

                // If the deleted version was the current version, switch to the current files mode
                if (currentVersion?.id === versionId) {
                    switchToCurrentFiles();
                }

                // Refresh versions list
                await fetchVersions();
            } else {
                onError(result.err);
            }
        } catch (error) {
            onError(`Failed to delete version: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to promote a version to working copy
    const promoteVersionToWorkingCopy = async (versionId, overwriteExisting = true) => {
        try {
            setIsLoading(true);

            if (!userActor || !userPrincipal || !projectId) {
                throw new Error("Required parameters not available");
            }

            // Call the backend method
            const result = await userActor.promoteVersionToWorkingCopy(
                userPrincipal,
                projectId,
                versionId,
                overwriteExisting
            );

            if ('ok' in result) {
                onSuccess(result.ok);
                // Switch to Current Files mode after promotion
                switchToCurrentFiles();
                // Refresh versions list
                await fetchVersions();
            } else {
                onError(result.err);
            }
        } catch (error) {
            onError(`Failed to promote version: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    const confirmDeleteVersion = (version) => {
        setVersionToDelete(version);
        setShowDeleteConfirmation(true);
    };

    const switchToCurrentFiles = () => {
        setCurrentVersion(null);
        setIsCurrentFilesMode(true);
        onVersionChange('current'); // Special value that your switchToVersion handler should recognize
    };

    // Function to create a new version
    const createNewVersion = async () => {
        try {
            setIsLoading(true);

            const result = await userActor.createProjectVersion(
                projectId,    // projectId
                Number(newVersion.major),
                Number(newVersion.minor),
                Number(newVersion.patch),
                newVersion.prerelease.length > 0 ? [newVersion.prerelease] : [], // Pass as an array with the string or empty array
                newVersion.build.length > 0 ? [newVersion.build] : [],           // Pass as an array with the string or empty array
                newVersion.description.length > 0 ? [newVersion.description] : [],
                newVersion.releaseNotes.length > 0 ? [newVersion.releaseNotes] : [],
                newVersion.parentVersionId.length > 0 ? [newVersion.parentVersionId] : []
            );

            if ('ok' in result) {
                onSuccess('Version created successfully');
                onVersionCreate(result.ok);
                setShowCreateModal(false);
                // Refresh the versions list
                await fetchVersions();
            } else {
                onError(result.err);
            }
        } catch (error) {
            onError(`Failed to create version: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to initialize the first project version
    const handleInitializeFirstVersion = async () => {
        try {
            setIsLoading(true);

            // Call the initializeProjectVersion function from props
            if (initializeProjectVersion) {
                await initializeProjectVersion();

                // Refresh the versions list after initialization
                await fetchVersions();
            } else {
                // Fallback if the function is not provided
                onError('Initialize project version function not available');
            }
        } catch (error) {
            onError(`Failed to initialize version: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Function to update version status
    const updateVersionStatus = async (versionId, newStatus) => {
        try {
            setIsLoading(true);

            // Call the backend method to update version status
            const result = await userActor.updateVersionStatus(versionId, newStatus);

            if ('ok' in result) {
                onSuccess(`Version status updated to ${newStatus}`);

                // Update local state for immediate UI feedback
                setVersions(prevVersions =>
                    prevVersions.map(v =>
                        v.id === versionId ? {...v, status: newStatus} : v
                    )
                );

                if (currentVersion?.id === versionId) {
                    setCurrentVersion({...currentVersion, status: newStatus});
                }

                // Call parent component handler
                if (onVersionStatusChange) {
                    onVersionStatusChange(versionId, newStatus);
                }

                // Refresh versions list
                await fetchVersions();
            } else {
                onError(result.err);
            }
        } catch (error) {
            onError(`Failed to update version: ${error instanceof Error ? error.message : String(error)}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form input changes for new version
    const handleInputChange = (e) => {
        const {name, value} = e.target;

        // Handle numeric fields
        if (['major', 'minor', 'patch'].includes(name)) {
            setNewVersion({
                ...newVersion,
                [name]: parseInt(value) || 0
            });
        } else {
            setNewVersion({
                ...newVersion,
                [name]: value
            });
        }
    };

    // Function to switch to a specific version
    const switchToVersion = (versionId) => {
        const version = versions.find(v => v.id === versionId);
        if (version) {
            setCurrentVersion(version);
            onVersionChange(versionId);
        }
    };

    // Function to check if a version is the base for working copy
    const isWorkingCopyBase = (versionId) => {
        return workingCopyBaseVersionId === versionId;
    };

    // Format semantic version to string (e.g. "1.2.3-beta+001")
    const formatVersion = (version) => {
        if (!version) return "Unknown";

        // Basic version number
        let result = `${version.major}.${version.minor}.${version.patch}`;

        // Only add prerelease if it exists and isn't empty
        if (version.prerelease && typeof version.prerelease === 'string' && version.prerelease.trim() !== '') {
            result += `-${version.prerelease}`;
        }

        // Only add build if it exists and isn't empty
        if (version.build && typeof version.build === 'string' && version.build.trim() !== '') {
            result += `+${version.build}`;
        }

        // Remove any "-+" pattern
        return result.replace('-+', '+');
    };

    // Format timestamp to human-readable date
    const formatDate = (timestamp) => {
        return new Date(Number(timestamp / BigInt(1000000))).toLocaleString();
    };

    // Reset new version form to default values based on current version
    const resetNewVersionForm = () => {
        if (currentVersion) {
            setNewVersion({
                major: currentVersion.semanticVersion.major,
                minor: currentVersion.semanticVersion.minor + 1, // Increment minor by default
                patch: 0, // Reset patch
                prerelease: '',
                build: '',
                description: '',
                releaseNotes: '',
                parentVersionId: currentVersion.id
            });
        }
        if (versions.length > 0) {
            setNewVersion({
                major: versions[0].semanticVersion.major,
                minor: versions[0].semanticVersion.minor + 1, // Increment minor from latest
                patch: 0, // Reset patch
                prerelease: '',
                build: '',
                description: '',
                releaseNotes: '',
                parentVersionId: versions[0].id
            });
        } else {
            setNewVersion({
                major: 1,
                minor: 0,
                patch: 0,
                prerelease: '',
                build: '',
                description: '',
                releaseNotes: '',
                parentVersionId: ''
            });
        }
    };

    // Show version details panel for a specific version
    const viewVersionDetails = (version) => {
        setSelectedVersion(version);
        setShowVersionDetails(true);
    };

    const getStatusString = (status) => {
        if (typeof status === 'object' && status !== null) {
            // Get the first key from the object
            const statusKey = Object.keys(status)[0];
            return statusKey ? statusKey.toLowerCase() : 'unknown';
        }
        return status;
    };
    return (
        <div style={{
            backgroundColor: "#fdf6e3",
            height: "100%",
            display: "flex",
            flexDirection: "column"
        }}>
            {/* Empty state message */}
            {!isLoading && versions.length === 0 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    padding: '40px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#5C4033',
                        marginBottom: '20px'
                    }}>
                        No versions found
                    </div>
                    <p style={{
                        color: '#8B4513',
                        marginBottom: '30px',
                        maxWidth: '450px',
                        fontSize: '16px'
                    }}>
                        Create your first version to start tracking changes to your project.
                    </p>
                    <button
                        onClick={handleInitializeFirstVersion}
                        style={{
                            backgroundColor: "#5C4033",
                            color: "#FFF5EE",
                            padding: "10px 20px",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontSize: '16px'
                        }}
                    >
                        Create First Version
                    </button>
                </div>
            )}

            {/* Loading indicator */}
            {isLoading && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3e5d8',
                        borderRadius: '50%',
                        borderTop: '4px solid #8B4513',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                </div>
            )}

            {/* Version List */}
            {!isLoading && versions.length > 0 && !showVersionDetails && (
                <div style={{flex: 1, overflow: 'auto', padding: '20px'}}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <thead>
                        <tr>
                            <th style={{
                                backgroundColor: '#f3e5d8',
                                textAlign: 'left',
                                padding: '12px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#5C4033',
                                borderBottom: '1px solid #e5d8c8'
                            }}>
                                Version
                            </th>
                            <th style={{
                                backgroundColor: '#f3e5d8',
                                textAlign: 'left',
                                padding: '12px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#5C4033',
                                borderBottom: '1px solid #e5d8c8'
                            }}>
                                Status
                            </th>
                            <th style={{
                                backgroundColor: '#f3e5d8',
                                textAlign: 'left',
                                padding: '12px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#5C4033',
                                borderBottom: '1px solid #e5d8c8'
                            }}>
                                Created
                            </th>
                            <th style={{
                                backgroundColor: '#f3e5d8',
                                textAlign: 'right',
                                padding: '12px 20px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#5C4033',
                                borderBottom: '1px solid #e5d8c8'
                            }}>
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {versions.map((version) => (
                            <tr key={version.id} style={{
                                backgroundColor: currentVersion?.id === version.id ? '#fdf6e3' : '#fff',
                                transition: 'background-color 0.2s'
                            }}>
                                <td style={{
                                    padding: '12px 20px',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <div style={{
                                        fontWeight: '500',
                                        color: '#5C4033',
                                        fontSize: '14px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        {formatVersion(version.semanticVersion)}

                                        {/* Add working copy badge */}
                                        {isWorkingCopyBase(version.id) && (
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 6px',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                backgroundColor: '#5C4033',
                                                color: '#FFF5EE',
                                                borderRadius: '4px',
                                                letterSpacing: '0.5px'
                                            }}>
                                                WORKING COPY BASE
                                            </span>
                                        )}
                                    </div>
                                    {version.description && (
                                        <div style={{
                                            fontSize: '13px',
                                            color: '#8B4513',
                                            marginTop: '4px'
                                        }}>
                                            {version.description.length > 60
                                                ? `${version.description.substring(0, 60)}...`
                                                : version.description}
                                        </div>
                                    )}
                                </td>
                                <td style={{
                                    padding: '12px 20px',
                                    borderBottom: '1px solid #e5e7eb'
                                }}>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        backgroundColor:
                                            getStatusString(version.status) === 'released' ? '#e2f1e8' :
                                                getStatusString(version.status) === 'development' ? '#e3f2fd' :
                                                    getStatusString(version.status) === 'deprecated' ? '#ffebee' : '#f3f4f6',
                                        color:
                                            getStatusString(version.status) === 'released' ? '#2d6a4f' :
                                                getStatusString(version.status) === 'development' ? '#1976d2' :
                                                    getStatusString(version.status) === 'deprecated' ? '#c62828' : '#6b7280'
                                    }}>
                                        {getStatusString(version.status)}
                                    </span>
                                </td>
                                <td style={{
                                    padding: '12px 20px',
                                    borderBottom: '1px solid #e5e7eb',
                                    color: '#5C4033',
                                    fontSize: '14px'
                                }}>
                                    {formatDate(version.created)}
                                </td>
                                <td style={{
                                    padding: '12px 20px',
                                    borderBottom: '1px solid #e5e7eb',
                                    textAlign: 'right'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        gap: '8px'
                                    }}>
                                        <button
                                            onClick={() => viewVersionDetails(version)}
                                            style={{
                                                padding: '4px 10px',
                                                fontSize: '12px',
                                                backgroundColor: '#f3e5d8',
                                                color: '#5C4033',
                                                border: '1px solid #8B4513',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Details
                                        </button>

                                        {/* Add the promote to working copy button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setVersionToPromote(version);
                                                setShowPromoteConfirmation(true);
                                            }}
                                            style={{
                                                padding: '4px 10px',
                                                fontSize: '12px',
                                                backgroundColor: '#e2f1e8',
                                                color: '#2d6a4f',
                                                border: '1px solid #2d6a4f',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            To Working Copy
                                        </button>

                                        {currentVersion?.id !== version.id && (
                                            <button
                                                onClick={() => switchToVersion(version.id)}
                                                style={{
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    backgroundColor: '#8B4513',
                                                    color: '#FFF5EE',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Switch
                                            </button>
                                        )}

                                        {currentVersion?.id === version.id && (
                                            <span
                                                style={{
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    backgroundColor: '#5C4033',
                                                    color: '#FFF5EE',
                                                    borderRadius: '4px',
                                                    display: 'inline-block'
                                                }}
                                            >
                                                Current
                                            </span>
                                        )}

                                        <button
                                            onClick={() => confirmDeleteVersion(version)}
                                            style={{
                                                padding: '4px 10px',
                                                fontSize: '12px',
                                                backgroundColor: '#ffebee',
                                                color: '#c62828',
                                                border: '1px solid #c62828',
                                                borderRadius: '4px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Version Details View */}
            {!isLoading && showVersionDetails && selectedVersion && (
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: '20px'
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                    }}>
                        <button
                            onClick={() => setShowVersionDetails(false)}
                            style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: '#8B4513',
                                padding: '8px 12px',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                        >
                            <span>←</span> Back to version list
                        </button>
                        <div style={{
                            display: 'flex',
                            gap: '10px'
                        }}>
                            {/* Add the promote to working copy button */}
                            <button
                                onClick={() => {
                                    setVersionToPromote(selectedVersion);
                                    setShowPromoteConfirmation(true);
                                }}
                                style={{
                                    backgroundColor: "#2d6a4f",
                                    color: "#FFF5EE",
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontWeight: "500"
                                }}
                            >
                                Promote to Working Copy
                            </button>

                            {currentVersion?.id !== selectedVersion.id && (
                                <button
                                    onClick={() => switchToVersion(selectedVersion.id)}
                                    style={{
                                        backgroundColor: "#8B4513",
                                        color: "#FFF5EE",
                                        padding: "8px 16px",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontWeight: "500"
                                    }}
                                >
                                    Switch to this version
                                </button>
                            )}
                            <button
                                onClick={() => confirmDeleteVersion(selectedVersion)}
                                style={{
                                    backgroundColor: "#DC2626",
                                    color: "#FFF5EE",
                                    padding: "8px 16px",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontWeight: "500"
                                }}
                            >
                                Delete Version
                            </button>
                        </div>
                    </div>

                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        overflow: 'hidden',
                        marginBottom: '20px'
                    }}>
                        <div style={{
                            backgroundColor: '#f3e5d8',
                            padding: '16px 20px',
                            borderBottom: '1px solid #e5d8c8'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: '#5C4033',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    Version {formatVersion(selectedVersion.semanticVersion)}

                                    {/* Add working copy badge */}
                                    {isWorkingCopyBase(selectedVersion.id) && (
                                        <span style={{
                                            display: 'inline-block',
                                            padding: '2px 6px',
                                            fontSize: '10px',
                                            fontWeight: 'bold',
                                            backgroundColor: '#5C4033',
                                            color: '#FFF5EE',
                                            borderRadius: '4px',
                                            letterSpacing: '0.5px'
                                        }}>
                                            WORKING COPY BASE
                                        </span>
                                    )}
                                </h3>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    backgroundColor:
                                        getStatusString(selectedVersion.status) === 'released' ? '#e2f1e8' :
                                            getStatusString(selectedVersion.status) === 'development' ? '#e3f2fd' :
                                                getStatusString(selectedVersion.status) === 'deprecated' ? '#ffebee' : '#f3f4f6',
                                    color:
                                        getStatusString(selectedVersion.status) === 'released' ? '#2d6a4f' :
                                            getStatusString(selectedVersion.status) === 'development' ? '#1976d2' :
                                                getStatusString(selectedVersion.status) === 'deprecated' ? '#c62828' : '#6b7280'
                                }}>
                                    {getStatusString(selectedVersion.status)}
                                </span>
                            </div>
                        </div>

                        <div style={{padding: '20px'}}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: '20px',
                                marginBottom: '20px'
                            }}>
                                <div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#8B4513',
                                        marginBottom: '5px',
                                        fontWeight: '500'
                                    }}>
                                        Created
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#5C4033'
                                    }}>
                                        {formatDate(selectedVersion.created)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#8B4513',
                                        marginBottom: '5px',
                                        fontWeight: '500'
                                    }}>
                                        Parent Version
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#5C4033'
                                    }}>
                                        {selectedVersion.parentVersion ?
                                            versions.find(v => v.id === selectedVersion.parentVersion)?.semanticVersion ?
                                                formatVersion(versions.find(v => v.id === selectedVersion.parentVersion).semanticVersion) :
                                                selectedVersion.parentVersion :
                                            'None (Initial Version)'}
                                    </div>
                                </div>
                            </div>

                            {selectedVersion.description && (
                                <div style={{marginBottom: '20px'}}>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#8B4513',
                                        marginBottom: '5px',
                                        fontWeight: '500'
                                    }}>
                                        Description
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#5C4033',
                                        padding: '12px',
                                        backgroundColor: '#fdf6e3',
                                        borderRadius: '4px',
                                        border: '1px solid #e5d8c8'
                                    }}>
                                        {selectedVersion.description}
                                    </div>
                                </div>
                            )}

                            {selectedVersion.releaseNotes && (
                                <div style={{marginBottom: '20px'}}>
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#8B4513',
                                        marginBottom: '5px',
                                        fontWeight: '500'
                                    }}>
                                        Release Notes
                                    </div>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#5C4033',
                                        padding: '12px',
                                        backgroundColor: '#fdf6e3',
                                        borderRadius: '4px',
                                        border: '1px solid #e5d8c8',
                                        whiteSpace: 'pre-line'
                                    }}>
                                        {selectedVersion.releaseNotes}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div style={{
                                    fontSize: '13px',
                                    color: '#8B4513',
                                    marginBottom: '5px',
                                    fontWeight: '500'
                                }}>
                                    Canisters ({selectedVersion.canisters.length})
                                </div>
                                <div style={{
                                    fontSize: '14px',
                                    color: '#5C4033',
                                    padding: '12px',
                                    backgroundColor: '#fdf6e3',
                                    borderRadius: '4px',
                                    border: '1px solid #e5d8c8',
                                    maxHeight: '150px',
                                    overflow: 'auto'
                                }}>
                                    {selectedVersion.canisters.length > 0 ? (
                                        <ul style={{
                                            margin: 0,
                                            paddingLeft: '20px'
                                        }}>
                                            {selectedVersion.canisters.map((canister, idx) => (
                                                <li key={idx} style={{
                                                    marginBottom: '5px'
                                                }}>
                                                    {canister.toString()}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p style={{margin: 0}}>No canisters</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Status change section */}
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '6px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            backgroundColor: '#f3e5d8',
                            padding: '16px 20px',
                            borderBottom: '1px solid #e5d8c8'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#5C4033'
                            }}>
                                Change Version Status
                            </h3>
                        </div>

                        <div style={{
                            padding: '20px',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '10px'
                        }}>
                            {selectedVersion.status !== 'development' && (
                                <button
                                    onClick={() => updateVersionStatus(selectedVersion.id, 'development')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        border: '1px solid #1976d2',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Set to Development
                                </button>
                            )}
                            {selectedVersion.status !== 'released' && (
                                <button
                                    onClick={() => updateVersionStatus(selectedVersion.id, 'released')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#e2f1e8',
                                        color: '#2d6a4f',
                                        border: '1px solid #2d6a4f',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Set to Released
                                </button>
                            )}
                            {selectedVersion.status !== 'deprecated' && (
                                <button
                                    onClick={() => updateVersionStatus(selectedVersion.id, 'deprecated')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#ffebee',
                                        color: '#c62828',
                                        border: '1px solid #c62828',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Set to Deprecated
                                </button>
                            )}
                            {selectedVersion.status !== 'draft' && (
                                <button
                                    onClick={() => updateVersionStatus(selectedVersion.id, 'draft')}
                                    style={{
                                        padding: '8px 12px',
                                        fontSize: '14px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#6b7280',
                                        border: '1px solid #6b7280',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Set to Draft
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Version Modal */}
            {showCreateModal && (
                <CreateVersionDialog
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onCreateVersion={(versionData) => {
                        // Add a wrapping function that calls createNewVersion with the data
                        if (versionData) {
                            setNewVersion(versionData);
                            createNewVersion();
                        }
                    }}
                    versions={versions}
                />
            )}

            {/* Delete Confirmation Dialog */}
            {showDeleteConfirmation && versionToDelete && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fdf6e3',
                        borderRadius: '6px',
                        padding: '24px',
                        width: '400px',
                        maxWidth: '90%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                        <h3 style={{
                            margin: '0 0 16px 0',
                            color: '#5C4033',
                            fontSize: '18px',
                            fontWeight: '500'
                        }}>
                            Delete Version?
                        </h3>
                        <p style={{
                            margin: '0 0 24px 0',
                            color: '#8B4513',
                            fontSize: '14px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to delete
                            version {formatVersion(versionToDelete.semanticVersion)}?<br/>
                            This action cannot be undone and will permanently remove all files associated with this
                            version.
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => {
                                    setShowDeleteConfirmation(false);
                                    setVersionToDelete(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    deleteVersion(versionToDelete.id);
                                    setShowDeleteConfirmation(false);
                                    setVersionToDelete(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#DC2626',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '14px'
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Promote Version Confirmation Dialog */}
            {showPromoteConfirmation && versionToPromote && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fdf6e3',
                        borderRadius: '6px',
                        padding: '24px',
                        width: '450px',
                        maxWidth: '90%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}>
                        <h3 style={{
                            margin: '0 0 16px 0',
                            color: '#5C4033',
                            fontSize: '18px',
                            fontWeight: '500'
                        }}>
                            Promote Version to Working Copy
                        </h3>
                        <p style={{
                            margin: '0 0 24px 0',
                            color: '#8B4513',
                            fontSize: '14px',
                            lineHeight: '1.5'
                        }}>
                            Are you sure you want to promote
                            version {formatVersion(versionToPromote.semanticVersion)} to be your working copy?<br/>
                            This will update your current project files to match this version.
                        </p>

                        <div style={{
                            marginBottom: '24px',
                            padding: '12px',
                            backgroundColor: '#fff',
                            borderRadius: '4px',
                            border: '1px solid #e5d8c8'
                        }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={overwriteExisting}
                                    onChange={(e) => setOverwriteExisting(e.target.checked)}
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                />
                                <span style={{
                                    fontSize: '14px',
                                    color: '#5C4033'
                                }}>
                                    Overwrite existing files in working copy
                                </span>
                            </label>
                            <p style={{
                                margin: '8px 0 0 24px',
                                fontSize: '12px',
                                color: '#8B4513',
                                fontStyle: 'italic'
                            }}>
                                {overwriteExisting
                                    ? 'All files will be replaced with the version files'
                                    : 'Only add files that do not already exist in your working copy'}
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => {
                                    setShowPromoteConfirmation(false);
                                    setVersionToPromote(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#8B4513',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '14px'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    promoteVersionToWorkingCopy(versionToPromote.id, overwriteExisting);
                                    setShowPromoteConfirmation(false);
                                    setVersionToPromote(null);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#5C4033',
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '14px'
                                }}
                            >
                                Promote
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add CSS for spinner animation */}
            <style dangerouslySetInnerHTML={{
                __html: `
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `
            }}/>
        </div>
    );
};

export default VersioningPanel;